import { Alert, Box, Button, Card, CardContent, CardHeader, Chip, Grid, IconButton, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tabs, Tooltip, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { apiConstants, get, post, postMultipart } from "../../common/constants";
import { FormProvider, RHFDateField, RHFFileField, RHFTextField, RHFDateRangePickerField, RHFSelectField, RHFSearchableSelectField } from "../../common/hook-form";
import RHFMultipleSelectField from "../../common/hook-form/RHFMultipleSelectField";
import { MerchantFilterCached } from "../../common/ProshipFormComponents";
import { useGetAllCourierPartnersCachedQuery } from "../../redux/commonApiCalls";
import _ from "lodash";
import ProzoDataGrid from "../../common/ProzoDataGrid";
import { formatCurrency, formatDate1 } from "../../common/util";
import { LoadingButton } from "@mui/lab";
import { useSnackbar } from "notistack";
import PageTitle from "../../components/PageTitle";
import MUIModal from "../../components/MuiModal";
import DownloadSampleFile from "../../components/DownloadSampleFile";
import DownloadSharpIcon from '@mui/icons-material/DownloadSharp';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import HistoryIcon from '@mui/icons-material/History';
import AssessmentIcon from '@mui/icons-material/Assessment';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import Loader from "../../common/Loader";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from 'yup';
import { convertDate } from "../../helpers/UtilityHelper";
import "../Wallet/theme.css";
import CopyIcon from "../../common/CopyIcon";

function MerchantCNUpload() {
    const [page, setPage] = useState(0);
    const [sizePerPage, setSizePerPage] = useState(10);
    const [data, setData] = useState([]);
    const [reload, setReload] = useState(0);
    const [loading, setLoading] = useState(false);
    const [uploadOpen, setUploadOpen] = useState(false);
    const [awbModalOpen, setAwbModalOpen] = useState(false);
    const [selectedAwbData, setSelectedAwbData] = useState(null);
    const [logsModalOpen, setLogsModalOpen] = useState(false);
    const [selectedLogs, setSelectedLogs] = useState([]);
    const [reportOpen, setReportOpen] = useState(false);

    // Filter form
    const filterMethods = useForm({
        defaultValues: {
            awbNumber: '',
            cnNumber: '',
            issuedBy: '',
            fromDate: '',
            toDate: '',
        }
    });
    const { handleSubmit: handleFilterSubmit, reset: resetFilter } = filterMethods;

    const [filterData, setFilterData] = useState({});

    const onFilterSubmit = (data) => {
        const filters = {};
        if (data.awbNumber) filters.awbNumber = data.awbNumber;
        if (data.cnNumber) filters.cnNumber = data.cnNumber;
        if (data.issuedBy) filters.issuedBy = data.issuedBy;
        if (data.fromDate) filters.issueDateGT = convertDate(data.fromDate);
        if (data.toDate) filters.issueDateLT = convertDate(data.toDate);
        setFilterData(filters);
        setPage(0);
    };

    const handleReset = () => {
        resetFilter({
            awbNumber: '',
            cnNumber: '',
            issuedBy: '',
            fromDate: '',
            toDate: '',
        });
        setFilterData({});
        setPage(0);
    };

    // Fetch data
    useEffect(() => {
        setLoading(true);
        const request = {
            ...filterData,
            pageNumber: page,
            pageSize: sizePerPage,
            sortField: "createdAt",
            sortOrder: "DESC",
        };
        post(apiConstants.MERCHANT_CN_GET_BY_FILTER, request).then((res) => {
            setData(res?.data?.responseObj || res?.data || []);
            setLoading(false);
        }).catch((err) => {
            console.log(err);
            setLoading(false);
        });
    }, [filterData, page, sizePerPage, reload]);

    const awbDetailColumns = [
        {
            field: 'awbNumber', headerName: 'AWB Number', flex: 1.2,
            renderCell: ({ row }) => <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>{row.awbNumber}<CopyIcon text={row.awbNumber} /></Box>,
        },
        {
            field: 'cnAmount', headerName: 'Credit Amount', flex: 0.8,
            renderCell: ({ row }) => row.cnAmount ? formatCurrency(row.cnAmount, 0) : '-',
        },
        { field: 'merchantName', headerName: 'Merchant', flex: 1 },
        {
            field: 'merchantShippingPrice', headerName: 'Merchant Price', flex: 0.8,
            renderCell: ({ row }) => row.merchantShippingPrice ? formatCurrency(row.merchantShippingPrice, 0) : '-',
        },
        {
            field: 'courierShippingPrice', headerName: 'Courier Price', flex: 0.8,
            renderCell: ({ row }) => row.courierShippingPrice ? formatCurrency(row.courierShippingPrice, 0) : '-',
        },
        { field: 'courierCPAccountCode', headerName: 'Courier', flex: 0.9 },
        { field: 'merchantZone', headerName: 'Merchant Zone', flex: 0.8 },
        { field: 'courierZone', headerName: 'Courier Zone', flex: 0.8, renderCell: ({ row }) => row.courierZone || '-' },
    ];

    const columns = [
        {
            field: 'cnDetails',
            headerName: 'CN Details',
            flex: 1.2,
            renderCell: ({ row }) => (
                <Box sx={{ py: 1 }}>
                    <Typography variant="subtitle2" fontWeight={600}>{row.cnNumber || '-'}</Typography>
                    <Typography variant="caption" color="text.secondary">
                        Amount: {row.cnAmount ? formatCurrency(row.cnAmount, 0) : '-'}
                    </Typography>
                    {/* <br /> */}
                    {/* <Typography variant="caption" color="text.secondary">
                        AWB Total: {row.additionalData?.TOTAL_CN_AMOUNT ? formatCurrency(row.additionalData.TOTAL_CN_AMOUNT, 0) : '-'}
                    </Typography> */}
                </Box>
            ),
        },
        {
            field: 'awbNumbers',
            headerName: 'AWB Numbers',
            flex: 2,
            renderCell: ({ row }) => {
                const awbs = row.awbNumbers || [];
                const total = awbs.length;
                if (total === 0) return '-';
                const visible = awbs.slice(0, 3);
                const remaining = total - visible.length;
                return (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, py: 1, alignItems: 'center' }}>
                        {visible.map((awb) => (
                            <Chip key={awb} label={awb} size="small" variant="outlined" sx={{ fontSize: '11px' }} />
                        ))}
                        {remaining > 0 && (
                            <Chip
                                label={`+${remaining} more`}
                                size="small"
                                color="primary"
                                sx={{ fontSize: '11px', cursor: 'pointer' }}
                                onClick={() => {
                                    setSelectedAwbData(row);
                                    setAwbModalOpen(true);
                                }}
                            />
                        )}
                        <CopyIcon text={awbs.join(',')} title="Copy all AWBs" />
                    </Box>
                );
            },
        },
        {
            field: 'issuedBy',
            headerName: 'Issued By',
            flex: 1,
            renderCell: ({ row }) => (
                <Box sx={{ py: 1 }}>
                    <Typography variant="subtitle2" fontWeight={600}>{row.issuedBy || '-'}</Typography>
                    <Typography variant="caption" color="text.secondary">
                        Issued: {row.issueDate ? formatDate1(row.issueDate, 'dd MMM, yyyy') : '-'}
                    </Typography>
                </Box>
            ),
        },
        {
            field: 'status',
            headerName: 'Status',
            flex: 0.7,
            renderCell: ({ row }) => (
                <Chip
                    label={row.status || '-'}
                    size="small"
                    sx={{
                        fontSize: '11px',
                        fontWeight: 600,
                        backgroundColor: row.status === 'UPLOADED' ? '#4caf50' : '#ffa500',
                        color: 'white',
                    }}
                />
            ),
        },
        {
            field: 'remark',
            headerName: 'Remark',
            flex: 1.2,
            renderCell: ({ row }) => (
                <Tooltip title={row.remark || ''}>
                    <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {row.remark || '-'}
                    </Typography>
                </Tooltip>
            ),
        },
        {
            field: 'createdBy',
            headerName: 'Created By',
            flex: 1.3,
            renderCell: ({ row }) => (
                <Box sx={{ py: 1 }}>
                    <Tooltip title={row.createdBy || ''}>
                        <Typography variant="subtitle2" fontWeight={600} sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {row.createdBy || '-'}
                        </Typography>
                    </Tooltip>
                    <Typography variant="caption" color="text.secondary">
                        Created: {row.createdAt ? formatDate1(row.createdAt, 'dd MMM, yyyy') : '-'}
                    </Typography>
                </Box>
            ),
        },
        {
            field: 'action',
            headerName: 'Action',
            flex: 0.6,
            renderCell: ({ row }) => {
                const link = row?.uploadedCSVFile?.signedLink;
                return (
                    <>
                        {link && (
                            <Tooltip title="Download CN CSV">
                                <IconButton color="primary" size="small" onClick={() => window.open(link)}>
                                    <DownloadSharpIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                        {row?.actionLogs?.length > 0 && (
                            <Tooltip title="View Logs">
                                <IconButton color="info" size="small" onClick={() => {
                                    setSelectedLogs(row.actionLogs);
                                    setLogsModalOpen(true);
                                }}>
                                    <HistoryIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                    </>
                );
            }
        },
    ];

    return (
        <>
            {loading && <Loader />}
            <PageTitle>AWB Level Credit Note</PageTitle>

            {/* Upload Modal */}
            <MUIModal
                open={uploadOpen}
                handleClose={() => setUploadOpen(false)}
                title="Upload AWB Level Credit Note"
                children={
                    <UploadAWBCreditNote
                        handleClose={() => setUploadOpen(false)}
                        setReload={setReload}
                    />
                }
            />

            {/* AWB Details Modal */}
            <MUIModal
                open={awbModalOpen}
                handleClose={() => { setAwbModalOpen(false); setSelectedAwbData(null); }}
                title={`AWB Details — ${selectedAwbData?.cnNumber || ''}`}
                children={
                    selectedAwbData && (
                        <ProzoDataGrid
                            columns={awbDetailColumns}
                            autoHeight={true}
                            rows={selectedAwbData?.additionalData?.ORDERS || []}
                            pagination={false}
                            hideFooterPagination={true}
                            filterMode="client"
                            rowHeight={60}
                            rowCount={selectedAwbData?.additionalData?.ORDERS?.length || 0}
                            getRowId={(row) => row.awbNumber}
                        />
                    )
                }
            />

            {/* Action Logs Modal */}
            <MUIModal
                open={logsModalOpen}
                handleClose={() => { setLogsModalOpen(false); setSelectedLogs([]); }}
                title="Action Logs"
                maxWidth='md'
                children={
                    <ProzoDataGrid
                        columns={[
                            { field: 'action', headerName: 'Action', width: 140 },
                            { field: 'actionedBy', headerName: 'Actioned By', width: 220 },
                            {
                                field: 'timestamp', headerName: 'Timestamp', width: 180,
                                renderCell: ({ row }) => row.timestamp ? formatDate1(row.timestamp, 'dd MMM, yyyy HH:mm') : '-',
                            },
                            { field: 'remark', headerName: 'Remark', width: 300 },
                        ]}
                        autoHeight={true}
                        rows={selectedLogs || []}
                        pagination={false}
                        hideFooterPagination={true}
                        filterMode="client"
                        rowHeight={50}
                        rowCount={selectedLogs?.length || 0}
                        getRowId={(row) => row.timestamp + row.action}
                    />
                }
            />

            {/* Generate AWB Report Modal */}
            <MUIModal
                open={reportOpen}
                handleClose={() => setReportOpen(false)}
                title="Generate AWB wise Report"
                maxWidth="md"
                children={
                    <GenerateAWBReport handleClose={() => setReportOpen(false)} />
                }
            />

            {/* Filters */}
            <Card sx={{ mb: 2 }}>
                <CardHeader
                    action={
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                                variant="contained"
                                size="small"
                                color="primary"
                                startIcon={<AssessmentIcon />}
                                onClick={() => setReportOpen(true)}
                            >
                                Generate AWB wise Report
                            </Button>
                            <Button
                                variant="contained"
                                size="small"
                                color="warning"
                                startIcon={<FileUploadIcon />}
                                onClick={() => setUploadOpen(true)}
                            >
                                Upload AWB CN
                            </Button>
                        </Box>
                    }
                />
                <CardContent>
                    <FormProvider methods={filterMethods} onSubmit={handleFilterSubmit(onFilterSubmit)}>
                        <Grid container spacing={1} alignItems="center">
                            <Grid item xs={12} md={2}>
                                <RHFTextField name="awbNumber" label="AWB Number" />
                            </Grid>
                            <Grid item xs={12} md={2}>
                                <RHFTextField name="cnNumber" label="CN Number" />
                            </Grid>
                            <Grid item xs={12} md={2}>
                                <RHFTextField name="issuedBy" label="Issued By" />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <RHFDateRangePickerField label="Issue Date Range" />
                            </Grid>
                            <Grid item xs={12} md={2} sx={{ display: 'flex', alignItems: 'center' }}>
                                <LoadingButton variant="contained" size="small" type="submit">
                                    Submit
                                </LoadingButton>
                                <Button
                                    onClick={handleReset}
                                    variant="contained"
                                    color="error"
                                    size="small"
                                    sx={{ ml: 1 }}
                                >
                                    Reset
                                </Button>
                            </Grid>
                        </Grid>
                    </FormProvider>
                </CardContent>
            </Card>

            {/* Data Grid */}
            <ProzoDataGrid
                columns={columns}
                autoHeight={true}
                rows={Array.isArray(data) ? data : (data?.data || [])}
                sizePerPage={sizePerPage}
                setSizePerPage={setSizePerPage}
                setPage={setPage}
                page={page}
                pagination={true}
                hideFooterPagination={false}
                filterMode="server"
                rowHeight={100}
                rowCount={Number.MAX_VALUE}
                getRowId={(row) => row?.id || row?._id || Math.random()}
            />
        </>
    );
}

export default MerchantCNUpload;

// Upload Form Component
function UploadAWBCreditNote({ handleClose, setReload }) {
    const { enqueueSnackbar } = useSnackbar();
    const [submitting, setSubmitting] = useState(false);
    const [uploadResult, setUploadResult] = useState(null);
    const [uploadError, setUploadError] = useState(null);
    const [activeTab, setActiveTab] = useState(0);

    const methods = useForm({
        resolver: yupResolver(
            Yup.object().shape({
                cnNumber: Yup.string().required("CN Number is required"),
                issuedBy: Yup.string().required("Issued By is required"),
                issueDate: Yup.string().required("Issue Date is required"),
                cnAmount: Yup.number().typeError("CN Amount must be a number").required("CN Amount is required"),
                csvFile: Yup.mixed().required("Please upload CSV file"),
            })
        ),
        defaultValues: {
            cnNumber: '',
            issuedBy: '',
            issueDate: '',
            cnAmount: '',
            remark: '',
            csvFile: null,
        }
    });

    const { handleSubmit, reset } = methods;

    const onSubmit = async (data) => {
        setSubmitting(true);
        setUploadResult(null);
        setUploadError(null);
        const formData = new FormData();
        formData.append('cnNumber', data.cnNumber);
        formData.append('issuedBy', data.issuedBy);
        formData.append('issueDate', convertDate(data.issueDate));
        formData.append('cnAmount', data.cnAmount);
        formData.append('remark', data.remark || '');
        formData.append('csvFile', data.csvFile[0]);

        try {
            const endpoint = activeTab === 0
                ? apiConstants.MERCHANT_CN_UPLOAD
                : apiConstants.MERCHANT_CN_UPLOAD_ALLOW_DUPLICATES;
            const res = await postMultipart(endpoint, formData);
            const resData = res?.data;
            const responseObj = resData?.responseObj;

            if (resData?.status === "SUCCESS") {
                // API can return status=SUCCESS with rejected=true (row validation failures)
                if (responseObj?.rejected === true) {
                    setUploadResult({
                        rejected: true,
                        rejectionMessage: responseObj.rejectionMessage,
                        errorReportUrl: responseObj.errorReportUrl,
                    });
                    enqueueSnackbar("Upload rejected — download the error report for details", { variant: 'warning' });
                } else if (responseObj?.creditNote) {
                    setUploadResult({
                        rejected: false,
                        creditNote: responseObj.creditNote,
                    });
                    enqueueSnackbar("Credit Note uploaded successfully", { variant: 'success' });
                    setReload(r => r + 1);
                } else {
                    setUploadError("Unexpected response from server");
                }
            } else {
                setUploadError(resData?.errorMsg || "Upload failed");
            }
        } catch (err) {
            console.log(err);
            const errMsg = err?.response?.data?.errorMsg || err?.response?.data?.message || "Some error occurred";
            setUploadError(errMsg);
        } finally {
            setSubmitting(false);
        }
    };

    // Render body based on state (error / success / form)
    const renderBody = () => {
        // Error state
        if (uploadError) {
            return (
                <Box sx={{ textAlign: 'center', py: 4, px: 2 }}>
                    <ErrorOutlineIcon sx={{ fontSize: 56, color: 'error.main', mb: 2 }} />
                    <Typography variant="h6" fontWeight={700} color="error.main" gutterBottom>
                        Upload Failed
                    </Typography>
                    <Box sx={{
                        bgcolor: '#fef2f2', border: '1px solid #fecaca', borderRadius: 2,
                        p: 2, mb: 3, mx: 'auto', maxWidth: 520, textAlign: 'left',
                    }}>
                        <Typography variant="body2" color="error.dark" sx={{ wordBreak: 'break-word' }}>
                            {uploadError}
                        </Typography>
                    </Box>
                    <Button variant="contained" size="large" onClick={() => setUploadError(null)}>
                        Close
                    </Button>
                </Box>
            );
        }

        // Result state — rejected (validation failures) or success (CN uploaded)
        if (uploadResult) {
            // ── Rejected: validation failures, download error report CSV ──
            if (uploadResult.rejected) {
                const { rejectionMessage, errorReportUrl } = uploadResult;
                return (
                    <Box sx={{ py: 2, px: 1 }}>
                        <Box sx={{ textAlign: 'center', mb: 2 }}>
                            <ErrorOutlineIcon sx={{ fontSize: 56, color: 'warning.main', mb: 1.5 }} />
                            <Typography variant="h6" fontWeight={700} color="warning.dark" gutterBottom>
                                Upload Rejected
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                No AWBs were saved. Fix the errors and re-upload.
                            </Typography>
                        </Box>

                        <Alert severity="warning" variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
                            {rejectionMessage || 'Upload rejected due to validation errors.'}
                        </Alert>

                        {errorReportUrl && (
                            <Box sx={{
                                border: '1px solid #fde68a', borderRadius: 2, p: 2, mb: 2,
                                bgcolor: '#fffbeb', display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap',
                            }}>
                                <ErrorOutlineIcon sx={{ color: 'warning.main' }} />
                                <Box sx={{ flex: 1, minWidth: 200 }}>
                                    <Typography variant="subtitle2" fontWeight={600} color="warning.dark">
                                        Error Report Available
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Contains the AWB numbers and exact reasons each row failed.
                                    </Typography>
                                </Box>
                                <Button
                                    variant="contained"
                                    color="warning"
                                    size="small"
                                    startIcon={<DownloadSharpIcon />}
                                    onClick={() => window.open(errorReportUrl, '_blank')}
                                >
                                    Download Error Report
                                </Button>
                            </Box>
                        )}

                        <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-start', mt: 2 }}>
                            <Button
                                variant="contained"
                                startIcon={<CloudUploadOutlinedIcon />}
                                onClick={() => { setUploadResult(null); reset(); }}
                            >
                                Upload Again
                            </Button>
                            <Button variant="outlined" onClick={handleClose}>
                                Close
                            </Button>
                        </Box>
                    </Box>
                );
            }

            // ── Success: CN uploaded cleanly ──
            const { creditNote } = uploadResult;
            const totalOrders = creditNote?.additionalData?.TOTAL_ORDERS || '-';
            const totalCnAmount = creditNote?.additionalData?.TOTAL_CN_AMOUNT;
            const csvLink = creditNote?.uploadedCSVFile?.signedLink;
            return (
                <Box sx={{ py: 3, px: 1 }}>
                    <Box sx={{ textAlign: 'center', mb: 3 }}>
                        <CheckCircleOutlineIcon sx={{ fontSize: 56, color: 'success.main', mb: 1.5 }} />
                        <Typography variant="h6" fontWeight={700} color="success.dark" gutterBottom>
                            Credit Note Uploaded Successfully
                        </Typography>
                        <Box sx={{ display: 'inline-flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center', mt: 1 }}>
                            {creditNote?.cnNumber && (
                                <Chip label={`CN: ${creditNote.cnNumber}`} color="primary" variant="outlined" />
                            )}
                            <Chip label={`${totalOrders} AWB(s) Processed`} color="success" variant="outlined" />
                            {totalCnAmount && (
                                <Chip label={`Total: ${formatCurrency(Number(totalCnAmount), 0)}`} color="info" variant="outlined" />
                            )}
                        </Box>
                    </Box>

                    {/* CN Details Summary */}
                    <Box sx={{ border: '1px solid #e5e7eb', borderRadius: 2, p: 2, mb: 2, bgcolor: '#fafafa' }}>
                        <Typography variant="subtitle2" fontWeight={600} gutterBottom>CN Details</Typography>
                        <Grid container spacing={1}>
                            <Grid item xs={6} md={4}><Typography variant="caption" color="text.secondary">Issued By</Typography><Typography variant="body2" fontWeight={600}>{creditNote?.issuedBy || '-'}</Typography></Grid>
                            <Grid item xs={6} md={4}><Typography variant="caption" color="text.secondary">Issue Date</Typography><Typography variant="body2" fontWeight={600}>{creditNote?.issueDate ? formatDate1(creditNote.issueDate, 'dd MMM, yyyy') : '-'}</Typography></Grid>
                            <Grid item xs={6} md={4}><Typography variant="caption" color="text.secondary">CN Amount</Typography><Typography variant="body2" fontWeight={600}>{creditNote?.cnAmount ? formatCurrency(creditNote.cnAmount, 0) : '-'}</Typography></Grid>
                            {creditNote?.remark && (
                                <Grid item xs={12}><Typography variant="caption" color="text.secondary">Remark</Typography><Typography variant="body2">{creditNote.remark}</Typography></Grid>
                            )}
                        </Grid>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', flexWrap: 'wrap', mt: 2 }}>
                        {csvLink && (
                            <Button
                                variant="outlined"
                                startIcon={<DownloadSharpIcon />}
                                onClick={() => window.open(csvLink, '_blank')}
                            >
                                Download Uploaded CSV
                            </Button>
                        )}
                        <Button
                            variant="contained"
                            startIcon={<CloudUploadOutlinedIcon />}
                            onClick={() => { setUploadResult(null); reset(); }}
                        >
                            Upload Another
                        </Button>
                        <Button variant="outlined" color="inherit" onClick={handleClose}>
                            Close
                        </Button>
                    </Box>
                </Box>
            );
        }

        // Default: upload form
        return (
            <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
                <Grid container spacing={1.5}>
                    <Grid item xs={12} md={6}>
                        <RHFTextField label="CN Number *" name="cnNumber" size="small" />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <RHFTextField label="Issued By *" name="issuedBy" size="small" />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <RHFDateField label="Issue Date *" name="issueDate" size="small" />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <RHFTextField label="CN Amount *" name="cnAmount" size="small" />
                    </Grid>
                    <Grid item xs={12}>
                        <RHFTextField label="Remark" name="remark" size="small" />
                    </Grid>
                    <Grid item xs={12}>
                        <RHFFileField label="CSV / XLSX File *" name="csvFile" fileType={['csv', 'xlsx']} />
                    </Grid>
                    <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        <LoadingButton loading={submitting} type="submit" variant="contained" size="small" startIcon={<CloudUploadOutlinedIcon />}>
                            Upload
                        </LoadingButton>
                        <Button onClick={() => reset()} variant="outlined" color="inherit" size="small">
                            Reset
                        </Button>
                        <DownloadSampleFile fileName="MERCHANT_CN_UPLOAD_CSV" />
                    </Grid>
                </Grid>
            </FormProvider>
        );
    };

    return (
        <>
            <Tabs
                value={activeTab}
                onChange={(e, v) => { setActiveTab(v); reset(); setUploadResult(null); setUploadError(null); }}
                sx={{ minHeight: 36, mb: 1.5, borderBottom: 1, borderColor: 'divider' }}
            >
                <Tab label="New — AWBs without CNs" sx={{ textTransform: 'none', fontWeight: 600, minHeight: 36, py: 0, fontSize: 13 }} />
                <Tab label="Existing — AWBs with CNs" sx={{ textTransform: 'none', fontWeight: 600, minHeight: 36, py: 0, fontSize: 13 }} />
            </Tabs>
            {renderBody()}
        </>
    );
}

// ─── Courier dropdown using prozoId as value (required by awb-report API) ───
function CourierProzoIdFilter() {
    const filter = {
        andfilter: [{ field: "is_deleted", operator: "eq", value: '0' }],
        offset: 0,
        limit: 1000,
        skipCount: true,
        includes: "id,name,cpAccountCode,prozoId,supportedShipmentType,isBlended,type",
    };
    const { data = [] } = useGetAllCourierPartnersCachedQuery(filter);
    const options = _.orderBy(data, 'name')
        .filter((val) => val.prozoId)
        .map((val) => ({
            value: val.prozoId,
            label: `${val.cpAccountCode} (${val.prozoId})`,
        }));
    return (
        <RHFSearchableSelectField
            fullWidth
            name="courier"
            label="Select Courier (optional)"
            options={options}
        />
    );
}

// ─── Generate AWB Report Component ───────────────────────────────────────────
function GenerateAWBReport({ handleClose }) {
    const { enqueueSnackbar } = useSnackbar();
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null);
    const [cnReasonsList, setCnReasonsList] = useState([]);

    const schema = Yup.object().shape({
        dateType: Yup.string().required('Select date range type'),
        fromDate: Yup.string().required('From date is required'),
        toDate: Yup.string().required('To date is required'),
    });

    const methods = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            dateType: 'issue',
            fromDate: '',
            toDate: '',
            merchant: '',
            courier: '',
            cnReasons: [],
            awbNumbers: '',
        },
    });
    const { handleSubmit, reset } = methods;

    // Fetch CN reasons once
    useEffect(() => {
        get(apiConstants.MERCHANT_CN_REASONS).then((res) => {
            const reasons = res?.data?.responseObj || res?.data || [];
            setCnReasonsList(Array.isArray(reasons) ? reasons : []);
        }).catch(() => {
            // silently fail — user can still submit without reasons filter
        });
    }, []);

    const onSubmit = (formData) => {
        // Validate date range
        const from = new Date(formData.fromDate);
        const to = new Date(formData.toDate);
        if (isNaN(from.getTime()) || isNaN(to.getTime())) {
            enqueueSnackbar('Invalid date range', { variant: 'error' });
            return;
        }
        const diffDays = Math.round((to - from) / (1000 * 60 * 60 * 24));
        if (diffDays < 0) {
            enqueueSnackbar('To date must be after from date', { variant: 'error' });
            return;
        }
        if (diffDays > 32) {
            enqueueSnackbar('Date range cannot exceed 32 days', { variant: 'error' });
            return;
        }

        const request = {};
        if (formData.dateType === 'issue') {
            request.issueDateStart = convertDate(formData.fromDate);
            request.issueDateEnd = convertDate(formData.toDate);
        } else {
            request.uploadDateStart = convertDate(formData.fromDate);
            request.uploadDateEnd = convertDate(formData.toDate);
        }
        if (formData.merchant) request.merchantOid = formData.merchant;
        if (formData.courier) request.courierProzoId = formData.courier;
        if (formData.cnReasons?.length) {
            // API expects descriptions — map enum keys to descriptions
            const descriptions = formData.cnReasons.map((enumKey) => {
                const match = cnReasonsList.find((r) => r.enum === enumKey);
                return match ? match.description : enumKey;
            });
            request.cnReasons = descriptions;
        }
        if (formData.awbNumbers) {
            const awbs = String(formData.awbNumbers)
                .split(/[\n,]/)
                .map((s) => s.trim())
                .filter(Boolean);
            if (awbs.length) request.awbNumbers = awbs;
        }

        setSubmitting(true);
        post(apiConstants.MERCHANT_CN_AWB_REPORT, request).then((res) => {
            const data = res?.data?.responseObj;
            const status = res?.data?.status;
            if (status === 'SUCCESS' && data?.reportUrl) {
                setResult({ reportUrl: data.reportUrl, rowCount: data.rowCount });
                window.open(data.reportUrl, '_blank');
                enqueueSnackbar(`Report generated (${data.rowCount} rows)`, { variant: 'success' });
            } else {
                enqueueSnackbar(res?.data?.errorMsg || 'Failed to generate report', { variant: 'error' });
            }
            setSubmitting(false);
        }).catch((err) => {
            const msg = err?.response?.data?.errorMsg || err?.response?.data?.message || err?.message || 'Failed to generate report';
            enqueueSnackbar(msg, { variant: 'error' });
            setSubmitting(false);
        });
    };

    if (result) {
        return (
            <Box sx={{ p: 1 }}>
                <Alert severity="success" icon={<CheckCircleOutlineIcon />} sx={{ mb: 2 }}>
                    Report generated with <strong>{result.rowCount}</strong> rows. Download should have started automatically.
                </Alert>
                <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center' }}>
                    <Button
                        variant="contained"
                        size="small"
                        startIcon={<DownloadSharpIcon />}
                        onClick={() => window.open(result.reportUrl, '_blank')}
                    >
                        Download Again
                    </Button>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() => { setResult(null); reset(); }}
                    >
                        Generate Another
                    </Button>
                    <Button
                        variant="outlined"
                        color="inherit"
                        size="small"
                        onClick={handleClose}
                    >
                        Close
                    </Button>
                </Box>
            </Box>
        );
    }

    return (
        <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={1.5}>
                <Grid item xs={12}>
                    <Alert severity="info" sx={{ fontSize: '0.8rem', py: 0.5 }}>
                        A date range is required (max 32 days). All other filters are optional.
                    </Alert>
                </Grid>
                <Grid item xs={12} md={4}>
                    <RHFSelectField
                        name="dateType"
                        label="Date Range Type *"
                        options={[
                            { value: 'issue', label: 'Issue Date' },
                            { value: 'upload', label: 'Upload Date' },
                        ]}
                    />
                </Grid>
                <Grid item xs={12} md={4}>
                    <RHFDateField name="fromDate" label="From Date *" size="small" />
                </Grid>
                <Grid item xs={12} md={4}>
                    <RHFDateField name="toDate" label="To Date *" size="small" />
                </Grid>
                <Grid item xs={12} md={6}>
                    <MerchantFilterCached />
                </Grid>
                <Grid item xs={12} md={6}>
                    <CourierProzoIdFilter />
                </Grid>
                <Grid item xs={12} md={6}>
                    <RHFMultipleSelectField
                        name="cnReasons"
                        label="CN Reasons"
                        width={400}
                        search
                        options={cnReasonsList.map((r) => ({ value: r.enum, label: r.description }))}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <RHFTextField
                        name="awbNumbers"
                        label="AWB Numbers (comma or newline separated)"
                        size="small"
                        multiline
                        rows={2}
                    />
                </Grid>
                <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                    <LoadingButton
                        loading={submitting}
                        type="submit"
                        variant="contained"
                        size="small"
                        startIcon={<AssessmentIcon />}
                    >
                        Generate Report
                    </LoadingButton>
                    <Button onClick={() => reset()} variant="outlined" color="inherit" size="small">
                        Reset
                    </Button>
                    <Button onClick={handleClose} variant="outlined" color="error" size="small">
                        Cancel
                    </Button>
                </Grid>
            </Grid>
        </FormProvider>
    );
}
